import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Server, Lock, Globe, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const DataProtection = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#D1FAE5]">
      {/* Header */}
      <div className="bg-[#064E3B] text-white py-6">
        <div className="container mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white hover:bg-white/10 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          <h1 className="text-3xl md:text-4xl font-bold">Data Protection</h1>
          <p className="text-gray-300 mt-2">Last updated: June 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12">
          
          <div className="flex items-center gap-3 mb-8">
            <Shield className="h-8 w-8 text-[#064E3B]" />
            <h2 className="text-2xl font-bold text-[#064E3B]">How We Protect Your Data</h2>
          </div>

          <div className="bg-[#D1FAE5] border border-[#84CC16] rounded-lg p-4 mb-8">
            <p className="text-[#064E3B] font-medium">
              Your financial data is sensitive, and we take its protection seriously. Here's how we keep your information safe.
            </p>
          </div>

          <div className="space-y-8 text-gray-700">
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Lock className="h-5 w-5 text-[#84CC16]" />
                <h3 className="text-xl font-semibold text-[#064E3B]">1. Encryption</h3>
              </div>
              <p className="mb-3">
                We use strong, industry-standard encryption to protect your data:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>In Transit:</strong> All data transmitted between your device and our servers is encrypted over HTTPS (TLS)</li>
                <li><strong>At Rest:</strong> Your data is stored in MongoDB Atlas, which encrypts stored data at rest (AES-256)</li>
                <li><strong>WhatsApp Messages:</strong> Messages travel over WhatsApp's encrypted transport. Once received by MoneyFeed through our messaging provider (Twilio), their contents are processed to extract your transactions and then stored securely</li>
              </ul>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-3">
                <Server className="h-5 w-5 text-[#84CC16]" />
                <h3 className="text-xl font-semibold text-[#064E3B]">2. Infrastructure Security</h3>
              </div>
              <p className="mb-3">
                Our infrastructure is built on reputable, security-focused cloud providers:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Hosted on managed cloud infrastructure (MongoDB Atlas and our application platform) whose providers maintain SOC 2 compliance</li>
                <li>Automated monitoring and access controls at the infrastructure level</li>
                <li>Managed, redundant backups handled by our database provider</li>
                <li>DDoS protection and TLS provided by our hosting and CDN layer</li>
              </ul>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-3">
                <Globe className="h-5 w-5 text-[#84CC16]" />
                <h3 className="text-xl font-semibold text-[#064E3B]">3. Compliance</h3>
              </div>
              <p className="mb-3">
                We design MoneyFeed to respect international data-protection rights:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-[#064E3B] mb-2">GDPR</h4>
                  <p className="text-sm">We honor GDPR rights for EU users, including access, export and deletion of your data</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-[#064E3B] mb-2">CCPA</h4>
                  <p className="text-sm">We honor CCPA rights for California users, including access and deletion, and we do not sell your data</p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-[#064E3B] mb-3">4. Access Controls</h3>
              <p className="mb-3">
                We limit who can access your data:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access to user data is restricted to authorized personnel who need it to operate the service</li>
                <li>Every account's data is scoped to its owner — users can only access their own records</li>
                <li>Accounts are protected by phone-number verification and a PIN stored only as a bcrypt hash</li>
                <li>Authentication endpoints are rate-limited to help prevent abuse</li>
              </ul>
            </section>

            <section>
              <div className="flex items-center gap-2 mb-3">
                <Trash2 className="h-5 w-5 text-[#84CC16]" />
                <h3 className="text-xl font-semibold text-[#064E3B]">5. Data Retention & Deletion</h3>
              </div>
              <p className="mb-3">
                You have control over your data:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Active Accounts:</strong> Data is retained as long as your account is active</li>
                <li><strong>Account Deletion:</strong> When you delete your account, all personal data is permanently removed within 30 days</li>
                <li><strong>Export:</strong> You can export all your data at any time from Settings</li>
                <li><strong>Inactive Accounts:</strong> Accounts inactive for 2+ years may be deleted after notification</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-[#064E3B] mb-3">6. Incident Response</h3>
              <p className="mb-3">
                In the unlikely event of a security incident:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>We investigate suspected security incidents promptly</li>
                <li>If a breach affects your personal data, we aim to notify affected users within 72 hours, in line with GDPR</li>
                <li>We take steps to remediate the issue and prevent recurrence</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-[#064E3B] mb-3">7. Questions or Concerns?</h3>
              <p>
                If you have any questions about how we protect your data, please contact us:
              </p>
              <div className="mt-3 p-4 bg-gray-50 rounded-lg">
                <p><strong>Email:</strong> feed@maxefeed.com</p>
                <p><strong>Phone:</strong> +1 (832) 688-6863</p>
                <p><strong>Address:</strong> 5900 Balcones Drive 7274, Austin, TX 78731</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataProtection;
